import React, {useCallback, useEffect, useRef, useState} from 'react';
import {FiBookmark, FiChevronDown, FiEdit2, FiSave, FiTrash2, FiX} from 'react-icons/fi';
import {useSavedSearchContext} from '../../contexts/saved_search_context';
import {
    buildFilterConfigFromMultiFilters,
    filterConfigToMultiFilters,
    MultiColumnFilter,
    SavedSearch,
    SearchFilterConfig
} from '../../schemas/saved_search';
import {TagFilter} from '../../schemas/boolean_filtering';
import {ColumnFilter} from './filter-types';
import {DropdownPortal} from './dropdown-portal';

interface SavedSearchSelectorProps {
    filters: ColumnFilter[];
    tagFilters: Map<string, TagFilter[]> | null;
    onApplySearch: (filters: ColumnFilter[], tagFilters: TagFilter[]) => void;
}

export const SavedSearchSelector: React.FC<SavedSearchSelectorProps> = ({
    filters,
    tagFilters,
    onApplySearch,
}) => {
    const {
        savedSearches,
        savedSearchLoading,
        createSavedSearch,
        updateSavedSearch,
        deleteSavedSearch,
    } = useSavedSearchContext() ?? {};

    const [isOpen, setIsOpen] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [showManageModal, setShowManageModal] = useState(false);
    const [saveName, setSaveName] = useState('');
    const [saveDescription, setSaveDescription] = useState('');
    const [editingSearch, setEditingSearch] = useState<SavedSearch | null>(null);
    const [editName, setEditName] = useState('');

    const triggerRef = useRef<HTMLButtonElement>(null);
    const saveModalRef = useRef<HTMLDivElement>(null);
    const manageModalRef = useRef<HTMLDivElement>(null);

    // Close modals on outside click and escape
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (saveModalRef.current && !saveModalRef.current.contains(e.target as Node)) {
                setShowSaveModal(false);
            }
            if (manageModalRef.current && !manageModalRef.current.contains(e.target as Node)) {
                setShowManageModal(false);
                setEditingSearch(null);
            }
        };

        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsOpen(false);
                setShowSaveModal(false);
                setShowManageModal(false);
                setEditingSearch(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    const hasActiveFilters = filters.some(f => f.value.trim().length > 0) || (tagFilters && tagFilters.size > 0);

    const handleApplySearch = useCallback((search: SavedSearch) => {
        // Convert saved filter config to ColumnFilter[] format
        const restoredFilters = filterConfigToMultiFilters(search.filter_config) as ColumnFilter[];
        onApplySearch(restoredFilters, search.filter_config.tag_filters);
        setIsOpen(false);
    }, [onApplySearch]);

    const handleSaveCurrentFilters = useCallback(async () => {
        if (!saveName.trim() || !createSavedSearch) return;

        const tagFiltersArray = tagFilters ? Array.from(tagFilters.values()).flat() : [];
        // Convert ColumnFilter[] to MultiColumnFilter[] format for saving
        const multiFilters: MultiColumnFilter[] = filters.map(f => ({
            id: f.id,
            columnId: f.columnId,
            value: f.value,
            searchType: f.searchType,
        }));
        const filterConfig = buildFilterConfigFromMultiFilters(multiFilters, tagFiltersArray);

        await createSavedSearch({
            name: saveName.trim(),
            description: saveDescription.trim() || undefined,
            filter_config: filterConfig,
        });

        setSaveName('');
        setSaveDescription('');
        setShowSaveModal(false);
    }, [saveName, saveDescription, filters, tagFilters, createSavedSearch]);

    const handleUpdateSearch = useCallback(async (search: SavedSearch) => {
        if (!editName.trim() || !updateSavedSearch) return;

        await updateSavedSearch({
            id: search.id,
            name: editName.trim(),
        });

        setEditingSearch(null);
        setEditName('');
    }, [editName, updateSavedSearch]);

    const handleDeleteSearch = useCallback(async (search: SavedSearch) => {
        if (!deleteSavedSearch) return;
        if (!confirm(`Delete saved search "${search.name}"?`)) return;

        await deleteSavedSearch(search.id);
    }, [deleteSavedSearch]);

    const getFilterSummary = (config: SearchFilterConfig): string => {
        const parts: string[] = [];
        if (config.document_filters.length > 0) {
            parts.push(`${config.document_filters.length} column filter${config.document_filters.length > 1 ? 's' : ''}`);
        }
        if (config.tag_filters.length > 0) {
            parts.push(`${config.tag_filters.length} tag filter${config.tag_filters.length > 1 ? 's' : ''}`);
        }
        return parts.length > 0 ? parts.join(', ') : 'No filters';
    };

    if (!savedSearches) {
        return null; // Context not available
    }

    return (
        <div className="relative">
            {/* Main button */}
            <button
                ref={triggerRef}
                className="flex items-center gap-1.5 rounded-xl border border-zinc-300 px-3 py-1.5 text-sm text-[#1c1c24] hover:bg-zinc-50"
                onClick={() => setIsOpen(!isOpen)}
            >
                <FiBookmark className="text-zinc-600" />
                <span>Saved Searches</span>
                {savedSearches.length > 0 && (
                    <span className="rounded-full bg-zinc-200 px-1.5 text-xs">{savedSearches.length}</span>
                )}
                <FiChevronDown className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {/* Dropdown - via portal */}
            <DropdownPortal
                triggerRef={triggerRef}
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                width={288}
                maxHeight={320}
            >
                {/* Save current filters */}
                {hasActiveFilters && (
                    <button
                        className="flex w-full items-center gap-2 border-b border-zinc-200 px-3 py-2 text-sm text-[#6d6d8a] hover:bg-[#ececf3]"
                        onClick={() => {
                            setShowSaveModal(true);
                            setIsOpen(false);
                        }}
                    >
                        <FiSave />
                        Save current filters...
                    </button>
                )}

                {/* Saved searches list */}
                <div className="max-h-64 overflow-y-auto">
                    {savedSearchLoading ? (
                        <div className="px-3 py-2 text-sm text-zinc-500">Loading...</div>
                    ) : savedSearches.length === 0 ? (
                        <div className="px-3 py-4 text-center text-sm text-zinc-500">
                            No saved searches yet.
                            {hasActiveFilters && (
                                <div className="mt-1 text-xs">Apply some filters and save them!</div>
                            )}
                        </div>
                    ) : (
                        savedSearches.map((search) => (
                            <button
                                key={search.id}
                                className="flex w-full flex-col gap-0.5 px-3 py-2 text-left hover:bg-zinc-50"
                                onClick={() => handleApplySearch(search)}
                            >
                                <div className="font-medium text-sm text-[#1c1c24]">{search.name}</div>
                                <div className="text-xs text-zinc-500">
                                    {getFilterSummary(search.filter_config)}
                                </div>
                                {search.description && (
                                    <div className="text-xs text-zinc-400 truncate">{search.description}</div>
                                )}
                            </button>
                        ))
                    )}
                </div>

                {/* Manage option */}
                {savedSearches.length > 0 && (
                    <button
                        className="flex w-full items-center gap-2 border-t border-zinc-200 px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50"
                        onClick={() => {
                            setShowManageModal(true);
                            setIsOpen(false);
                        }}
                    >
                        <FiEdit2 />
                        Manage saved searches...
                    </button>
                )}
            </DropdownPortal>

            {/* Save Modal */}
            {showSaveModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
                    <div ref={saveModalRef} className="w-96 rounded-xl bg-white p-4 shadow-xl">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="font-semibold text-[#1c1c24]">Save Current Filters</h3>
                            <button onClick={() => setShowSaveModal(false)} className="text-zinc-400 hover:text-zinc-600">
                                <FiX className="w-[1.25em] h-[1.25em]" />
                            </button>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="mb-1 block text-sm font-medium text-[#1c1c24]">Name *</label>
                                <input
                                    type="text"
                                    className="w-full rounded border border-zinc-300 px-3 py-2 text-sm text-[#1c1c24] focus:border-[#8c8ca3] focus:outline-none"
                                    placeholder="e.g., Q1 Contract Review"
                                    value={saveName}
                                    onChange={(e) => setSaveName(e.target.value)}
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="mb-1 block text-sm font-medium text-[#1c1c24]">Description (optional)</label>
                                <textarea
                                    className="w-full rounded border border-zinc-300 px-3 py-2 text-sm text-[#1c1c24] focus:border-[#8c8ca3] focus:outline-none"
                                    placeholder="Brief description of this search"
                                    rows={2}
                                    value={saveDescription}
                                    onChange={(e) => setSaveDescription(e.target.value)}
                                />
                            </div>
                            <div className="text-xs text-zinc-500">
                                This will save: {getFilterSummary(
                                    buildFilterConfigFromMultiFilters(
                                        filters.map(f => ({
                                            id: f.id,
                                            columnId: f.columnId,
                                            value: f.value,
                                            searchType: f.searchType,
                                        })),
                                        tagFilters ? Array.from(tagFilters.values()).flat() : []
                                    )
                                )}
                            </div>
                        </div>

                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                className="rounded-lg border border-zinc-300 px-4 py-2 text-sm text-[#1c1c24] hover:bg-zinc-50"
                                onClick={() => setShowSaveModal(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="rounded-lg bg-[#8c8ca3] px-4 py-2 text-sm text-white hover:bg-[#7c7c95] disabled:opacity-50"
                                onClick={handleSaveCurrentFilters}
                                disabled={!saveName.trim()}
                            >
                                Save
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Manage Modal */}
            {showManageModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
                    <div ref={manageModalRef} className="w-[28rem] rounded-xl bg-white p-4 shadow-xl">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="font-semibold text-[#1c1c24]">Manage Saved Searches</h3>
                            <button onClick={() => setShowManageModal(false)} className="text-zinc-400 hover:text-zinc-600">
                                <FiX className="w-[1.25em] h-[1.25em]" />
                            </button>
                        </div>

                        <div className="max-h-80 space-y-2 overflow-y-auto">
                            {savedSearches.map((search) => (
                                <div key={search.id} className="flex items-center gap-2 rounded-lg border border-zinc-200 p-2">
                                    {editingSearch?.id === search.id ? (
                                        <>
                                            <input
                                                type="text"
                                                className="flex-1 rounded border border-zinc-300 px-2 py-1 text-sm text-[#1c1c24] focus:border-[#8c8ca3] focus:outline-none"
                                                value={editName}
                                                onChange={(e) => setEditName(e.target.value)}
                                                autoFocus
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleUpdateSearch(search);
                                                    if (e.key === 'Escape') setEditingSearch(null);
                                                }}
                                            />
                                            <button
                                                className="rounded p-1 text-[#6d6d8a] hover:bg-[#ececf3]"
                                                onClick={() => handleUpdateSearch(search)}
                                                title="Save"
                                            >
                                                <FiSave className="w-[1em] h-[1em]" />
                                            </button>
                                            <button
                                                className="rounded p-1 text-zinc-600 hover:bg-zinc-100"
                                                onClick={() => setEditingSearch(null)}
                                                title="Cancel"
                                            >
                                                <FiX className="w-[1em] h-[1em]" />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <div className="flex-1">
                                                <div className="font-medium text-sm text-[#1c1c24]">{search.name}</div>
                                                <div className="text-xs text-zinc-500">
                                                    {getFilterSummary(search.filter_config)}
                                                </div>
                                            </div>
                                            <button
                                                className="rounded p-1 text-zinc-600 hover:bg-zinc-100"
                                                onClick={() => {
                                                    setEditingSearch(search);
                                                    setEditName(search.name);
                                                }}
                                                title="Rename"
                                            >
                                                <FiEdit2 className="w-[1em] h-[1em]" />
                                            </button>
                                            <button
                                                className="rounded p-1 text-red-600 hover:bg-red-50"
                                                onClick={() => handleDeleteSearch(search)}
                                                title="Delete"
                                            >
                                                <FiTrash2 className="w-[1em] h-[1em]" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="mt-4 flex justify-end">
                            <button
                                className="rounded-lg border border-zinc-300 px-4 py-2 text-sm text-[#1c1c24] hover:bg-zinc-50"
                                onClick={() => setShowManageModal(false)}
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SavedSearchSelector;